import React from "react";
import {
  TextField as Field,
  Box,
  Paper,
  Typography,
  useTheme,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  TextField,
  Drawer,
  IconButton,
  Collapse,
  Divider,
  TableFooter,
} from "@mui/material";
import { Container, styled } from "@mui/system";
import { useState, useEffect, useCallback } from "react";
import { Button, Title, useDataProvider, useNotify, Link, usePermissions } from "react-admin";
import { Cancel, CheckCircle, ChevronRight, Delete, Info, Person, Visibility } from "@mui/icons-material";
import { IALoading } from "../components/IAProgress";
import { SendGridIcon } from "./img/SendGridIcon";
import { permissionCheck } from "../api/permissionManager";

// Styled components
const StyledContainer = styled(Container)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(4),
  minHeight: "100vh",
  alignItems: "center",
  flexDirection: "column",
  display: "flex",
}));
const StyledField = styled(Field)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: theme.palette.background.paper,
    "& fieldset": { borderColor: theme.palette.divider },
    "&:hover fieldset": { borderColor: theme.palette.text.secondary },
  },
  "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
}));
const ClearButton = styled(Button)(({ theme }) => ({
  borderRadius: "8px",
  textTransform: "none",
  borderColor: theme.palette.divider,
  color: theme.palette.text.primary,
  "&:hover": {
    borderColor: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
  },
}));
const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: "bold",
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

// Reusable Delete Button Component
const DeleteActionButton = ({
  label,
  confirmTitle,
  confirmText,
  onDelete,
  variant = "contained",
  isGroupUnsubscribe = false,
  email,
  groupName,
  type,
}) => {
  const [open, setOpen] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState("");
  const [error, setError] = useState("");

  // Check if the input matches the email
  const isConfirmed = confirmationInput.trim().toLowerCase() === email.trim().toLowerCase();

  const handleConfirmClick = () => {
    if (isConfirmed) {
      onDelete();
      setOpen(false);
      setConfirmationInput("");
      setError("");
    } else setError("Email does not match. Please type the exact recipient email.");
  };

  const handleDialogClose = () => {
    setOpen(false);
    setConfirmationInput("");
    setError("");
  };

  const finalConfirmText = (
    <>
      <DialogContentText sx={{ fontWeight: "bold", color: "error.main" }}>
        You are about to delete a status for {email}
      </DialogContentText>
      <DialogContentText sx={{ mt: 2 }}>
        {isGroupUnsubscribe ? `This will remove the "${groupName}" unsubscribe status for ${email}.` : confirmText}
      </DialogContentText>
      <DialogContentText sx={{ mt: 2, fontWeight: "bold" }}>
        You MUST verify that {email} has explicitly requested this action.
      </DialogContentText>
      <DialogContentText sx={{ mt: 1 }}>
        Please confirm you have directly contacted {email} (the recipient) and received their approval for this
        deletion. Performing this action without their consent may violate security policies.
      </DialogContentText>
    </>
  );

  return (
    <>
      <Tooltip title={`Delete this ${isGroupUnsubscribe ? "group unsubscribe" : type} for ${email}`} arrow>
        <span>
          <Button
            startIcon={<Delete />}
            label={label}
            variant={variant}
            color="error"
            size="small"
            onClick={() => setOpen(true)}
            sx={{ textTransform: "none" }}
          />
        </span>
      </Tooltip>
      <Dialog open={open} onClose={handleDialogClose}>
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          {finalConfirmText}
          <TextField
            label={`Type "${email}" to confirm`}
            variant="outlined"
            value={confirmationInput}
            fullWidth
            margin="normal"
            error={!!error}
            helperText={error || "Enter the exact email address to enable confirmation."}
            onChange={(e) => {
              setConfirmationInput(e.target.value);
              setError("");
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleDialogClose} label="Cancel" />
          <Button
            variant="contained"
            onClick={handleConfirmClick}
            label="Confirm - I have verified with recipient"
            color="error"
            disabled={!isConfirmed}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export const SendGridList = () => {
  const [email, setEmail] = useState("");
  const [input, setInput] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [backendUserId, setBackendUserId] = useState(null);
  const [scanningUserId, setScanningUserId] = useState(null);
  const theme = useTheme();
  const dataProvider = useDataProvider();
  const { permissions } = usePermissions();
  const notify = useNotify();

  useEffect(() => {
    const cachedData = localStorage.getItem("sendGridSearchCache");
    if (cachedData) {
      const { email, statuses, backendUserId, scanningUserId } = JSON.parse(cachedData);
      setEmail(email || "");
      setInput(email || "");
      setStatuses(statuses || []);
      setBackendUserId(backendUserId || null);
      setScanningUserId(scanningUserId || null);
    }
  }, []);

  useEffect(() => {
    const cacheData = { email, statuses, backendUserId, scanningUserId };
    localStorage.setItem("sendGridSearchCache", JSON.stringify(cacheData));
  }, [email, statuses, backendUserId, scanningUserId]);

  const handleSearch = async () => {
    if (!input.trim()) {
      setEmail("");
      setStatuses([]);
      setBackendUserId(null);
      setScanningUserId(null);
      setError(null);
      return;
    }
    setEmail(input);
    setLoading(true);
    setError(null);
    try {
      let isBackendUser = false;
      if (permissionCheck("backend_users", "read_only", permissions)) {
        const backendResponse = await dataProvider.getOne("backend_users", { id: input });
        isBackendUser = backendResponse.data.email;
        setBackendUserId(isBackendUser ? backendResponse.data.id : null);
      } else setBackendUserId(null);

      let isScanningUser = false;
      if (permissionCheck("customers", "read_only", permissions)) {
        const scanningResponse = await dataProvider.getOne("scanning_users", { id: input });
        isScanningUser = scanningResponse.data.email;
        setScanningUserId(isScanningUser ? scanningResponse.data.id : null);
      } else setScanningUserId(null);

      const response = await dataProvider.getOne("sendgrid", { id: input, meta: { isFreeLoaderSearch: true } });
      setStatuses(response.data.statuses || []);
    } catch (err) {
      setError("Failed to fetch data. Please double check the recipient email and try again.");
      setStatuses([]);
      setBackendUserId(null);
      setScanningUserId(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setEmail("");
    setInput("");
    setStatuses([]);
    setError(null);
    setBackendUserId(null);
    setScanningUserId(null);
    localStorage.removeItem("sendGridSearchCache");
  };

  const handleDelete = async (type, groupId = null) => {
    try {
      const params = groupId
        ? { id: email, group_id: groupId, meta: type }
        : { id: email, isFreeLoaderSearch: true, meta: type };
      await dataProvider.delete("sendgrid", params);
      // Refetch the latest statuses from the server
      const response = await dataProvider.getOne("sendgrid", { id: email, meta: { isFreeLoaderSearch: true } });
      setStatuses(response.data.statuses || []);
      notify(`${type} removed successfully for ${email}`, { type: "success" });
    } catch (error) {
      notify(`Failed to remove ${type}: ${error}`, { type: "error" });
    }
  };

  const renderStatuses = () => {
    if (loading)
      return (
        <div style={{ marginTop: "4em" }}>
          <IALoading size={80} />
        </div>
      );
    if (error)
      return (
        <Typography variant="body1" color="error" sx={{ mt: 4 }}>
          {error}
        </Typography>
      );
    if (statuses.length === 0 && email)
      return (
        <Typography variant="body1" sx={{ mt: 4 }}>
          No active statuses found for {email}.
        </Typography>
      );

    const filteredStatuses = statuses.filter(
      (status) => !(status.type === "group unsubscribe" && status.suppressed === false)
    );

    const renderName = (type) => {
      switch (type) {
        case "unsubscribe":
          return "Global Unsubscribe";
        case "group unsubscribe":
          return "Group Unsubscribe";
        case "bounce":
          return "Bounce";
        case "block":
          return "Block";
        default:
          return type;
      }
    };

    const getTooltipText = (type) => {
      switch (type) {
        case "group unsubscribe":
          return "User unsubscribed from a specific type of email from us.";
        case "unsubscribe":
          return "User unsubscribed from all emails from us.";
        case "block":
          return "Email rejected due to message issues.";
        case "bounce":
          return "Email undeliverable and returned.";
        default:
          return "";
      }
    };

    const getDeleteProps = (status) => {
      switch (status.type) {
        case "block":
          return {
            label: "Delete",
            confirmTitle: "Clear All Blocks?",
            confirmText: "This will remove ALL blocks",
            onDelete: () => handleDelete("Blocks"),
            email: status.email,
            type: status.type,
          };
        case "bounce":
          return {
            label: "Delete",
            confirmTitle: "Clear All Bounces?",
            confirmText: "This will remove ALL bounces",
            onDelete: () => handleDelete("Bounce"),
            email: status.email,
            type: status.type,
          };
        case "unsubscribe":
          return {
            label: "Delete",
            confirmTitle: "Clear Global Unsubscribe?",
            confirmText: "This will remove the global unsubscribe",
            onDelete: () => handleDelete("Unsubscribe"),
            email: status.email,
            type: status.type,
          };
        case "group unsubscribe":
          return {
            label: "Delete",
            confirmTitle: "Remove Group Unsubscribe?",
            onDelete: () => handleDelete("Group Unsubscribe", status.suppression_group_id),
            isGroupUnsubscribe: true,
            email: status.email,
            groupName: status.name || "this group",
            type: status.type,
          };
        default:
          return null;
      }
    };

    const getReason = (status) => {
      return status.type === "group unsubscribe" ? `The email account is unsubscribed from ${status.name}` : "-";
    };

    return (
      <TableContainer component={Paper} sx={{ maxWidth: "lg", mt: 4 }}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <StyledTableHeadCell sx={{ textAlign: "center" }}>Recipient Email</StyledTableHeadCell>
              {backendUserId && <StyledTableHeadCell sx={{ textAlign: "center" }}>Backend User</StyledTableHeadCell>}
              {scanningUserId && <StyledTableHeadCell sx={{ textAlign: "center" }}>Scanning User</StyledTableHeadCell>}
              <StyledTableHeadCell>Created</StyledTableHeadCell>
              <StyledTableHeadCell>Type</StyledTableHeadCell>
              <StyledTableHeadCell>Reason</StyledTableHeadCell>
              <StyledTableHeadCell sx={{ textAlign: "center" }}>Actions</StyledTableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStatuses.map((status, index) => {
              const deleteProps = getDeleteProps(status);
              return (
                <StyledTableRow key={index}>
                  <TableCell sx={{ textAlign: "center" }}>{status.email}</TableCell>
                  {backendUserId && (
                    <TableCell sx={{ textAlign: "center" }}>
                      <Tooltip title="View Backend User" arrow>
                        <span>
                          <Link to={`/backend_users/${backendUserId}/show`}>
                            <Visibility color="primary" />
                          </Link>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                  {scanningUserId && (
                    <TableCell sx={{ textAlign: "center" }}>
                      <Tooltip title="View Scanning User" arrow>
                        <span>
                          <Link to={`/scanning_users/${scanningUserId}/show`}>
                            <Visibility color="primary" />
                          </Link>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell>{status.created || "-"}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography sx={{ flexGrow: 1 }}>{renderName(status.type)}</Typography>
                      <Tooltip title={getTooltipText(status.type)} arrow placement="top">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>{status.reason || getReason(status)}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {permissionCheck("sendgrid", "write", permissions) ? (
                      deleteProps ? (
                        <DeleteActionButton {...deleteProps} />
                      ) : (
                        "-"
                      )
                    ) : (
                      <Tooltip title={"You do not have permission to delete"} arrow placement="top">
                        <Info fontSize="small" color="error" />
                      </Tooltip>
                    )}
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <StyledContainer component={Paper} elevation={3}>
      <Title title="SendGrid Status Lookup" />
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 4, color: theme.palette.text.primary }}>
        SendGrid Status Lookup
      </Typography>
      <Box sx={{ width: "100%", maxWidth: 400, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <StyledField
            label="Recipient Email"
            variant="outlined"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            size="small"
            fullWidth
          />
          <Button
            label="Search"
            variant="contained"
            color="primary"
            onClick={handleSearch}
            sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
          />
          {input && <ClearButton label="Clear" variant="outlined" onClick={handleClear} />}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
          Search to view and manage SendGrid statuses for an email.
        </Typography>
      </Box>
      {email ? (
        renderStatuses()
      ) : (
        <Typography variant="body1" sx={{ mt: 4, color: theme.palette.text.secondary, fontStyle: "italic" }}>
          Enter a recipient's email address above to get started.
        </Typography>
      )}
    </StyledContainer>
  );
};

export const SendGridActivityList = () => {
  const [emailInput, setEmailInput] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const theme = useTheme();
  const dataProvider = useDataProvider();

  const calculateDefaultDates = useCallback(() => {
    const now = new Date();
    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(now.getDate() - 7);
    return {
      startDate: oneWeekAgo.toISOString(),
      endDate: now.toISOString(),
    };
  }, []);

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = date.getUTCHours();
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    return `${year}/${month}/${day} ${hours12}:${minutes}${ampm} UTC+00:00`;
  };

  useEffect(() => {
    if (!startDate || !endDate) {
      const { startDate: defaultStart, endDate: defaultEnd } = calculateDefaultDates();
      setStartDate(defaultStart);
      setEndDate(defaultEnd);
    }
  }, []);

  const getStatus = (msgStatus) =>
    msgStatus === "delivered" ? (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <CheckCircle sx={{ marginRight: "1rem" }} color="success" />
        {msgStatus}
      </div>
    ) : (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <Cancel sx={{ marginRight: "1rem" }} color="error" />
        {msgStatus}
      </div>
    );

  const handleSearch = async () => {
    if (!emailInput) return setError("You must enter an email");
    setError("");
    setLoading(true);

    // Manually construct the query string with proper encoding
    const queryValue = `to_email LIKE "%${emailInput}%") AND (last_event_time BETWEEN TIMESTAMP "${startDate}" AND TIMESTAMP "${endDate}"`;

    // Sendgrid expects an oddly formatted query string.
    // In order to avoid double encoding the internal query, use encodeURIComponent and then replace %20 with +
    const encodedQuery = encodeURIComponent(queryValue).replace(/%20/g, "+"); // Replace %20 with +

    const query_string = `limit=1000&query=%28${encodedQuery}%29`;

    try {
      const response = await dataProvider.getList("sendgrid", { query_string });
      setMessages(response.data);
    } catch (error) {
      setError(`Error searching email activity: ${JSON.stringify(error.message)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setError("");
    setEmailInput("");
    setMessages([]);
    const { startDate: defaultStart, endDate: defaultEnd } = calculateDefaultDates();
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
  };

  const handleRowClick = (msg) => {
    setSelectedMessage(msg);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedMessage(null);
  };

  const renderActivity = () => (
    <>
      {loading && (
        <div style={{ marginTop: "4em" }}>
          <IALoading size={80} />
        </div>
      )}
      {error && (
        <Typography variant="body1" color="error" sx={{ mt: 4 }}>
          {error}
        </Typography>
      )}
      {!loading && !error && (
        <TableContainer component={Paper} sx={{ maxWidth: "lg", mt: 4 }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <StyledTableHeadCell sx={{ textAlign: "center" }}>Status</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ textAlign: "center" }}>Message</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ textAlign: "center" }}>Last Event Received</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ textAlign: "center" }}>Opens</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ textAlign: "center" }}>Clicks</StyledTableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((msg) => {
                return (
                  <StyledTableRow
                    key={msg.msg_id}
                    onClick={() => handleRowClick(msg)}
                    sx={{ cursor: "pointer", "&:hover": { backgroundColor: "grey.100" } }}
                  >
                    <TableCell sx={{ textAlign: "center", paddingBottom: "45px" }}>{getStatus(msg.status)}</TableCell>
                    <TableCell>
                      <Typography style={{ fontWeight: "600", marginBottom: "5px" }}>To: {msg.to_email}</Typography>
                      <Typography>{msg.subject}</Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>{formatDate(msg.last_event_time)}</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>{msg.opens_count}</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>{msg.clicks_count}</TableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );

  return (
    <StyledContainer component={Paper} elevation={3}>
      <Title title="SendGrid Activity History" />
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 4, color: theme.palette.text.primary }}>
        SendGrid Activity History
      </Typography>
      <Box sx={{ width: "100%", maxWidth: 400, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
          <StyledField
            label="To Email Address"
            variant="outlined"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            size="small"
            fullWidth
            required
          />
          <StyledField
            label="Start Date (UTC)"
            type="datetime-local"
            variant="outlined"
            value={startDate.slice(0, -1)}
            onChange={(e) => setStartDate(e.target.value + "Z")}
            size="small"
            fullWidth
          />
          <StyledField
            label="End Date (UTC)"
            type="datetime-local"
            variant="outlined"
            value={endDate.slice(0, -1)}
            onChange={(e) => setEndDate(e.target.value + "Z")}
            size="small"
            fullWidth
          />
          <div style={{ display: "flex", flexDirection: "row", gap: "1rem" }}>
            <Button
              label="Search"
              variant="contained"
              color="primary"
              onClick={handleSearch}
              sx={{ borderRadius: "8px", textTransform: "none", padding: "6px 18px", width: "120px", height: "40px" }}
            />
            <ClearButton
              label="Clear"
              variant="outlined"
              onClick={handleClear}
              sx={{ borderRadius: "8px", textTransform: "none", padding: "6px 18px", width: "120px", height: "40px" }}
            />
          </div>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
          Search to view SendGrid email activity history.
        </Typography>
      </Box>
      {loading || error || messages.length > 0 ? (
        renderActivity()
      ) : (
        <>
          <Typography variant="body1" sx={{ mt: 4, color: theme.palette.text.secondary, fontStyle: "italic" }}>
            Enter an email address (or partial email) above to get started.
          </Typography>
          <Typography variant="body2" sx={{ mt: 4, color: theme.palette.text.secondary, fontStyle: "italic" }}>
            Please note: This tool is limited to only search the past <b>30</b> days
          </Typography>
        </>
      )}
      <Drawer
        closeAfterTransition={true}
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        sx={{ "& .MuiDrawer-paper": { width: { sm: "45%" }, boxSizing: "border-box", px: "2.5rem", py: "2.5rem" } }}
      >
        {selectedMessage && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ ml: 5 }}>
              Email Information
            </Typography>
            <Button label="Close" variant="outlined" onClick={handleDrawerClose} sx={{ padding: "6px 18px", mr: 4 }} />
          </Box>
        )}
        {selectedMessage && <SendGridActivityShow msg={selectedMessage} />}
      </Drawer>
    </StyledContainer>
  );
};

export const SendGridActivityShow = ({ msg }) => {
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const dataProvider = useDataProvider();

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "Invalid Date";
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = date.getUTCHours();
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    return `${year}/${month}/${day} ${hours12}:${minutes}${ampm} UTC+00:00`;
  };

  const capitalizeFirstLetter = (val) => {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
  };

  const getEventStatus = (eventName) =>
    ["delivered", "processed"].includes(eventName) ? (
      <CheckCircle sx={{ marginRight: "0.5rem" }} color="success" />
    ) : (
      <Cancel sx={{ marginRight: "0.5rem" }} color="error" />
    );

  const getEventDescription = (event) =>
    event.mx_server ? (
      <Typography>
        <strong>Received by {event.mx_server}</strong>
      </Typography>
    ) : (
      <Typography>
        <strong>Received by SendGrid</strong>
      </Typography>
    );

  const getEventMessage = (event) =>
    event.mx_server
      ? "This email was received by the customer's server and delivered to the recipient."
      : `SendGrid sent the email using the dedicated IP address ${message.outbound_ip}`;

  const fetchMessage = async () => {
    setLoading(true);
    try {
      const response = await dataProvider.getOne("sendgrid", { id: msg.msg_id, meta: { activity: true } });
      setMessage(response.data);
    } catch (error) {
      setError(`Failed to fetch message details: ${JSON.stringify(error.message)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessage();
  }, [msg]);

  if (loading)
    return (
      <Container sx={{ mt: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <IALoading />
      </Container>
    );
  if (error)
    return (
      <Container sx={{ mt: 2 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  if (!message)
    return (
      <Container sx={{ mt: 2 }}>
        <Typography>No message data available</Typography>
      </Container>
    );

  const events = message.events || [];

  return (
    <Container sx={{ position: "relative" }}>
      <Typography sx={{ mt: 2, mb: 1, fontSize: "13px", fontWeight: "bold" }}>Details</Typography>
      <Paper variant="outlined" sx={{ width: "100%", mb: 4 }}>
        <Table sx={{ tableLayout: "fixed" }}>
          <TableBody>
            <TableRow>
              <TableCell>
                <b>To</b>
              </TableCell>
              <TableCell colSpan={3}>{message.to_email}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <b>From</b>
              </TableCell>
              <TableCell colSpan={3}>{message.from_email}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <b>Subject</b>
              </TableCell>
              <TableCell colSpan={3}>{message.subject}</TableCell>
            </TableRow>
            {showMoreDetails && (
              <>
                <TableRow>
                  <TableCell>
                    <b>API Key</b>
                  </TableCell>
                  <TableCell colSpan={3}>{message.api_key_id}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <b>Message ID</b>
                  </TableCell>
                  <TableCell colSpan={3}>{message.msg_id}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <b>Sending IP Address</b>
                  </TableCell>
                  <TableCell colSpan={3}>{message.originating_ip}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <b>Categories</b>
                  </TableCell>
                  <TableCell colSpan={3}>
                    {message.categories?.map((cat, idx) => (
                      <Typography sx={{ fontSize: "0.875rem", lineHeight: 1.2, m: 0, p: 0 }}>{cat}</Typography>
                    ))}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <b>Unique Args</b>
                  </TableCell>
                  <TableCell>
                    {Object.entries(message.unique_args).map(([key, value], idx) => (
                      <Typography key={idx} sx={{ fontSize: "0.875rem", lineHeight: 1.2, m: 0, p: 0 }}>
                        {key}
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell colSpan={2}>
                    {Object.entries(message.unique_args).map(([key, value], idx) => (
                      <Typography key={idx} sx={{ fontSize: "0.875rem", lineHeight: 1.2, m: 0, p: 0 }}>
                        {value}
                      </Typography>
                    ))}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell
                colSpan={4}
                sx={{ backgroundColor: "#f9f9f9", textAlign: "center", cursor: "pointer", py: 2 }}
                onClick={() => setShowMoreDetails((prev) => !prev)}
              >
                <Typography variant="body2" sx={{ color: "primary.main" }}>
                  {showMoreDetails ? "Fewer Details" : "More Details"}
                </Typography>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
      <Typography sx={{ my: 1, fontSize: "13px", fontWeight: "bold" }}>Event History</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
        {events.map((event, index) => {
          const isExpanded = expanded === `panel${index}`;

          return (
            <React.Fragment key={index}>
              <Paper variant="outlined" sx={{ width: "100%" }}>
                <Box
                  data-testid={`event-${index}-description`}
                  sx={{ display: "flex", alignItems: "center", py: 2, px: 2 }}
                >
                  <Box sx={{ width: "50px", textAlign: "center" }}>{index === 0 ? <SendGridIcon /> : null}</Box>
                  <Box sx={{ flexGrow: 1, ml: 2 }}>{getEventDescription(event)}</Box>
                </Box>
                <Divider />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => setExpanded(isExpanded ? false : `panel${index}`)}
                >
                  <Box
                    data-testid={`event-${index}-status-${event.event_name}`}
                    sx={{ display: "flex", alignItems: "center", ml: 1 }}
                  >
                    {getEventStatus(event.event_name)}
                    <Typography sx={{ ml: 1, fontSize: "0.875rem" }}>
                      {capitalizeFirstLetter(event.event_name)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography data-testid={`event-${index}-date`} sx={{ mr: 1, fontSize: "0.875rem" }}>
                      {formatDate(event.processed)}
                    </Typography>
                    <IconButton data-testid={`event-${index}-${isExpanded ? "collapse" : "expand"}`} size="small">
                      <ChevronRight
                        sx={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
                      />
                    </IconButton>
                  </Box>
                </Box>
                <Collapse in={isExpanded}>
                  <Divider />
                  <Box data-testid={`event-${index}-msg-event`} sx={{ px: 2, py: 2 }}>
                    <Typography variant="body2">{getEventMessage(event)}</Typography>
                  </Box>
                </Collapse>
              </Paper>
              {index < events.length - 1 && (
                <Box sx={{ width: "2px", height: "2em", backgroundColor: "#ccc", alignSelf: "start", ml: "4rem" }} />
              )}
            </React.Fragment>
          );
        })}
        <Box sx={{ width: "2px", height: "2em", backgroundColor: "#ccc", alignSelf: "start", ml: "4rem" }} />
        <Paper variant="outlined" sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2 }}>
            <Box sx={{ width: "50px", textAlign: "center" }}>
              <Person />
            </Box>
            <Box sx={{ flexGrow: 1, ml: 2 }}>
              <Typography data-testid={`received-by-${message.to_email}`} sx={{ fontWeight: "bold" }}>
                Received by {message.to_email}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
